/**
 * Data formatting utilities for CCMS components
 */

import dayjs from 'dayjs';

// Render the backend-localized wall-clock verbatim by stripping any trailing 'Z'/±hh:mm offset
// (no re-zoning to the browser). Naive/date-only strings have nothing to strip.
const ccmsParse = (date: Date | string) =>
  typeof date === 'string' ? dayjs(date.replace(/(Z|[+-]\d{2}:?\d{2})$/, '')) : dayjs(date);

/**
 * Format file size in bytes to human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Format number with thousands separator
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

/**
 * Format currency
 */
export function formatCurrency(value: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(value);
}

/**
 * Format percentage
 */
export function formatPercentage(value: number, decimals: number = 2): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Format date.
 *
 * Preserves the backend-localized wall-clock: ISO strings keep their embedded offset (no re-zoning
 * to the browser) via dayjs.parseZone. The backend already converts every datetime to the
 * recipient's timezone, so the UI just renders it.
 */
export function formatDate(date: Date | string, format: string = 'short'): string {
  const d = ccmsParse(date);

  if (format === 'short') {
    return d.format('M/D/YYYY');
  } else if (format === 'long') {
    return d.format('MMMM D, YYYY');
  } else if (format === 'time') {
    return d.format('hh:mm A');
  } else if (format === 'datetime') {
    return d.format('MMM D, YYYY, hh:mm A');
  }

  return d.format('M/D/YYYY');
}

/**
 * Format time ago (e.g., "2 hours ago")
 */
export function formatTimeAgo(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const seconds = Math.floor((now.getTime() - d.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days > 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

/**
 * Truncate text to specified length
 */
export function truncateText(text: string, maxLength: number, suffix: string = '...'): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - suffix.length) + suffix;
}

/**
 * Get initials from name
 */
export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].substring(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Capitalize first letter of each word
 */
export function capitalizeWords(text: string): string {
  return text.replace(/\b\w/g, (char) => char.toUpperCase());
}
